import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, Landmark, Receipt, Save, Trash2, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { PrestationIdentite } from '../components/prestations/PrestationParts';
import { FinancementTab } from './dossier/FinancementTab';
import { FacturationTab } from './dossier/FacturationTab';
import { TYPE_PRESTATION, STATUT_PRESTATION } from '../lib/prestations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bloc = ({ icon: Icon, title, action, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base flex items-center gap-2"><Icon className="w-4 h-4 text-fuchsia-600" /> {title}</CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export default function PrestationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [societes, setSocietes] = useState([]);
  const [financeurs, setFinanceurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [dfVersion, setDfVersion] = useState(0);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/prestations/${id}`);
      setP(res.data);
      setForm(res.data);
    } catch { toast.error('Prestation introuvable'); navigate('/prestations'); }
  }, [id, navigate]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/societes`).then(r => setSocietes(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/financeurs`).then(r => setFinanceurs(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/clients`).then(r => setClients(r.data || [])).catch(() => {});
  }, [load]);

  if (!p || !form) return <DashboardLayout><div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></DashboardLayout>;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.intitule.trim()) return toast.error("L'intitulé est obligatoire");
    setSaving(true);
    try {
      const { planning, date_debut, date_fin, nb_jours, nb_heures, ca, nb_factures, ...body } = form;
      const res = await axios.put(`${API_URL}/prestations/${id}`, body);
      setP(res.data); setForm(res.data); setEdit(false);
      toast.success('Prestation enregistrée — planning des collaborateurs mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API_URL}/prestations/${id}`);
      toast.success('Prestation supprimée');
      navigate('/prestations');
    } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); setDelOpen(false); }
  };

  const typeMeta = TYPE_PRESTATION[p.type_prestation] || {};
  const statutMeta = STATUT_PRESTATION[p.statut] || {};
  const formDataFacturation = { intitule: p.intitule, code_action_client: p.code_action_client, date_debut: p.date_debut, date_fin: p.date_fin, planning: [], candidats: [] };

  return (
    <DashboardLayout>
    <div className="space-y-5" data-testid="prestation-detail-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/prestations')} data-testid="prestation-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-fuchsia-700" data-testid="prestation-code">{p.code}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${typeMeta.cls}`}>{typeMeta.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statutMeta.cls}`} data-testid="prestation-statut-badge">{statutMeta.label}</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900">{p.intitule}</h1>
            <p className="text-sm text-slate-500">{p.client || 'Client non renseigné'}{p.lieu ? ` · ${p.lieu}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {edit ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setForm(p); setEdit(false); }}><X className="w-4 h-4 mr-1" /> Annuler</Button>
              <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700" disabled={saving} onClick={save} data-testid="prestation-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer</>}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDelOpen(true)} data-testid="prestation-delete"><Trash2 className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="prestation-edit"><Pencil className="w-4 h-4 mr-1" /> Modifier</Button>
            </>
          )}
        </div>
      </div>

      <Bloc icon={Briefcase} title="Mission" testId="prestation-bloc-mission">
        {edit ? <PrestationIdentite form={form} set={set} societes={societes} /> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div><p className="text-[10px] uppercase text-slate-400">Contact</p><p>{p.contact || '—'}</p></div>
            <div><p className="text-[10px] uppercase text-slate-400">Réf. client</p><p>{p.code_action_client || '—'}</p></div>
            <div><p className="text-[10px] uppercase text-slate-400">Projet commercial</p><p>{p.projet_commercial_id ? <button type="button" className="text-indigo-600 underline" onClick={() => navigate(`/commercial?projet=${p.projet_commercial_id}&filtre=tous`)}>Voir le projet</button> : '—'}</p></div>
            <div className="md:col-span-3"><p className="text-[10px] uppercase text-slate-400">Description</p><p className="whitespace-pre-line text-slate-700">{p.description || '—'}</p></div>
          </div>
        )}
      </Bloc>

      <Bloc icon={Landmark} title="Dossier financeur / client payeur" testId="prestation-bloc-financement">
        <FinancementTab dossierId={id} formData={formDataFacturation} financeurs={financeurs} formations={[]} modules={[]} clients={clients} onDfsChanged={() => { setDfVersion(v => v + 1); load(); }} />
      </Bloc>

      <Bloc icon={Receipt} title="Devis & factures (saisie libre, référence aux bons de commande)" testId="prestation-bloc-facturation">
        <FacturationTab dossierId={id} formData={formDataFacturation} financeurs={financeurs} refreshKey={dfVersion} mode="prestation" />
      </Bloc>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Supprimer {p.code} ?</AlertDialogTitle><AlertDialogDescription>Les affectations planning liées seront retirées. Impossible si des devis ou factures existent.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="prestation-delete-confirm">Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </DashboardLayout>
  );
}
